import React, { useEffect } from 'react';
/**
 * `Confirmation` renders the purchase confirmation page for dealer journeys.
 *
 * Behaviour:
 * - Marks confirmation step complete via `onPageChange`.
 * - Optionally marks an existing quote as "bought" via `QuotesApi.saveQuoteBought`
 *   when quotes are enabled.
 * - Renders a read-only `SummaryPlan` and confirmation messaging.
 * - Provides a "New search" action that navigates back to the landing page.
 */

import { Helmet } from 'react-helmet';
import SummaryPlan from '../common/Summary/SummaryPlan';
import { PageAbsolutePath } from '../../consts/Common';
import Confirm from '../common/Confirmation/Confirm';
import PageHeader from '../common/PageHeader';
import { useJourney } from './Layout';
import { ButtonBack } from '../common/Button/ButtonBack';
import { useNavigate } from 'react-router-dom';
import { BoughtProductsDto, QuoteApiResponse } from '../common/CommonTypes';
import { QuotesApi } from '../../api/QuotesApi';
import { getSelectedProducts } from '../common/ProductMethods';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../common/HelperMethods';
import { useAppSelector } from '../../app-state/Hooks';

const Confirmation = (): JSX.Element => {
  const { journey, onPageChange } = useJourney();
  const {
    products,
    personalDetail,
    orderNumbers,
    quoteId,
    foundExistingQuotes
  } = journey;

  const appConfig = useAppSelector((s) => s.appConfig);
  const userToken = useAppSelector((s) => s.user.token);
  const dealerCode = useAppSelector((s) => s.user.dealerCode);
  const confirmationPage = useAppSelector((s) => s.content.confirmationPage);
  const cms = confirmationPage;

  const { t } = useTranslation();

  const referenceText = t('confirmation.order-numbers', {
    orderNumbervalues: orderNumbers.join(' & '),
    interpolation: { escapeValue: false }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    onPageChange(PageAbsolutePath.Confirmation, true);
    updateQuotes();
  }, []);

  const navigate = useNavigate();
  const updateQuotes = () => {
    if (appConfig.includeQuotes && foundExistingQuotes && quoteId > 0) {
      const controller = new AbortController();

      const successCallback = (data: QuoteApiResponse) => {
        if (data.isSaved) {
          console.log('Quote saved');
        }
      };

      const failureCallback = () => {
        console.log('Error occured in quote api save...');
      };

      const selectedProducts = getSelectedProducts(products);

      const quotesBought: BoughtProductsDto = {
        bought: true,
        dealerNumber: dealerCode,
        quoteId: quoteId,
        productTypeId: selectedProducts.map((x) => x.productTypeId)
      };

      new QuotesApi().saveQuoteBought(
        quotesBought,
        userToken,
        successCallback,
        failureCallback,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    }
  };

  const emailConfirmationText = cms.emailConfirmation
    ? cms.emailConfirmation.replace('{EMAILADDRESS}', personalDetail.email)
    : '';

  const onNewSearch = () => {
    const path = setCountryCode(
      PageAbsolutePath.Landing,
      appConfig.countryCode
    );
    navigate(path);
  };

  const nonce =
    document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content') ??
    '';

  return (
    <>
      <Helmet>
        <title>{t('page-title.confirmation')}</title>
        <script nonce={nonce} type='text/javascript'>
          {cms.force24Script}
        </script>
      </Helmet>

      <PageHeader
        title={cms.title}
        description={cms.description}
        information={cms.information}
        warning={cms.warning}
        descriptionClass='p2'
      />
      <Confirm
        subTitle={cms.summaryDescription}
        emailConfirmationText={emailConfirmationText}
        orderNumbers={orderNumbers}
        referenceText={referenceText}>
        <div className='product-selection-card-container m2-bottom'>
          <SummaryPlan
            products={products}
            showRemove={false}
            showTitle={false}
            showEdit={false}
            culture={appConfig.cultureCode}
          />
        </div>
      </Confirm>
      <ButtonBack
        name='btnNewSearch'
        onClick={onNewSearch}
        text={t('customer-button.button-new-search')}
      />
    </>
  );
};

export default Confirmation;
